import type { Styles } from '@adornis/ass/style.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { Bundesland } from '@chancenwerk/enums/enums.js';
import { PartnerAPI } from '@chancenwerk/shared/master-data-management/partner/api/PartnerAPI.js';
import { PartnerFilter } from '@chancenwerk/shared/master-data-management/partner/api/subscribePartnerByFilter.js';
import type { Partner } from '@chancenwerk/shared/master-data-management/partner/entities/Partner.js';
import { subscribeWorkshopAttendanceCount } from '@chancenwerk/shared/workshops/api/subscribeWorkshopAttendanceCount.js';
import { WorkshopAttendanceCount } from '@chancenwerk/shared/workshops/entities/WorkshopAttendanceCount.js';
import { WorkshopFilter } from '@chancenwerk/shared/workshops/entities/WorkshopFilter.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';

/**
 * @customElement akademie-component-workshop-rag-by-workshop
 */
@customElement('akademie-component-workshop-rag-by-workshop')
export class AkademieComponentWorkshopRAGByWorkshop extends ChemistryLitElement {
  @state() private readonly _workshopFilter = new RXController(this, new WorkshopFilter({ workshopID: '' }));

  override styles(): Styles[] {
    return [...super.styles(), { ':host': { overflow: 'auto' } }];
  }

  override render() {
    return html` <cr-card space="md" padding="md" ${css({ overflow: 'auto', maxWidth: '100%' })}>
      <chancen-component-card-title> je Workshop-Statistik </chancen-component-card-title>
      <cr-text bold>Filter</cr-text>
      ${this._filter()}
      <cr-text bold>Einträge</cr-text>
      ${this._table()}
    </cr-card>`;
  }

  private _filter() {
    return html` <cr-dropdown-multiselection
        clearable
        placeholder="Kooperationspartner"
        icon="business"
        .value=${this._workshopFilter.value.partners}
        .renderString=${(partner: Maybe<Partner>) => {
          if (!partner) return '';
          return partner.toString();
        }}
        .search=${(search: string) =>
          firstValueFrom(
            PartnerAPI.subscribeByFilter(
              0,
              20,
              new PartnerFilter({ search }),
            )({ name: 1, kuerzel: 1, address: { zipCity: { city: 1 } } }),
          )}
        @value-picked=${async (e: ValueEvent<Maybe<Partner[]>>) => {
          this._workshopFilter.value.partners = e.detail.value ?? [];
          this._workshopFilter.next(this._workshopFilter.value);
        }}
      >
      </cr-dropdown-multiselection>
      <cr-dropdown-selection
        placeholder="Bundesland"
        .selectables=${Object.values(Bundesland)}
        .value=${this._workshopFilter.value.bundesland}
        @value-picked=${(e: ValueEvent<Maybe<Bundesland>>) => {
          this._workshopFilter.value.bundesland = e.detail.value;
          this._workshopFilter.next(this._workshopFilter.value);
        }}
        clearable
      >
      </cr-dropdown-selection>
      <cr-grid columns="1fr 1fr" space="md">
        <cr-date-picker-dropdown
          .end-year=${DateTime.now().plus({ years: 10 }).year}
          placeholder="von"
          click-to-save
          flex
          .value=${this._workshopFilter.value.from}
          @value-picked=${(e: ValueEvent<DateTime>) => {
            this._workshopFilter.value.from = e.detail.value;
            if (!this._workshopFilter.value.till || this._workshopFilter.value.till < e.detail.value) {
              this._workshopFilter.value.till = e.detail.value.endOf('month');
            }
            this._workshopFilter.next(this._workshopFilter.value);
          }}
        ></cr-date-picker-dropdown>
        <cr-date-picker-dropdown
          .end-year=${DateTime.now().plus({ years: 10 }).year}
          placeholder="bis"
          click-to-save
          flex
          .value=${this._workshopFilter.value.till}
          @value-picked=${(e: ValueEvent<DateTime>) => {
            this._workshopFilter.value.till = e.detail.value;
            if (!this._workshopFilter.value.from || this._workshopFilter.value.from > e.detail.value) {
              this._workshopFilter.value.from = e.detail.value.startOf('month');
            }
            this._workshopFilter.next(this._workshopFilter.value);
          }}
        ></cr-date-picker-dropdown>
      </cr-grid>`;
  }

  private _table() {
    return html`
      <cr-data-view
        ${css({ overflowX: 'auto' })}
        .options=${{
          class: WorkshopAttendanceCount,
          operation: subscribeWorkshopAttendanceCount,
          view: new View<typeof WorkshopAttendanceCount>({ filter: this._workshopFilter.value }),
          isColumnResizable: true,
          onlyShowLastTranslation: true,
          customColumns: [
            {
              columnName: 'Workshop',
              columnWidth: 'max-content',
              customID: 'workshop',
              selectionSet: { workshopID: 1, workshop: { title: 1 } },
              getFieldMapping: w => w.workshop?.title,
            },
            {
              columnName: 'Termine',
              customID: 'eventcount',
              selectionSet: { eventCount: 1 },
              getFieldMapping: w => w.eventCount,
            },
            {
              columnName: 'Teilnehmeranzahl',
              customID: 'attendance-count',
              selectionSet: { attendanceCount: 1 },
              getFieldMapping: w => w.attendanceCount,
            },
            {
              columnName: 'durchschnittlich',
              customID: 'average',
              selectionSet: { attendanceCount: 1, eventCount: 1 },
              getFieldMapping: w => ((w.attendanceCount ?? 0) / (w.eventCount ?? 0)).toFixed(0),
            },
            {
              columnName: 'Minimum',
              customID: 'minimum',
              selectionSet: { attendanceCountMin: 1 },
              getFieldMapping: w => w.attendanceCountMin,
            },
            {
              columnName: 'Maximum',
              customID: 'maximum',
              selectionSet: { attendanceCountMax: 1 },
              getFieldMapping: w => w.attendanceCountMax,
            },
          ],
        } as DataViewOptions<typeof WorkshopAttendanceCount>}
      >
      </cr-data-view>
    `;
  }
}
